import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "./AuthContext";

const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, buyer_discount_percent: 0 });
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!user || user.role !== "buyer") return;
    setLoading(true);
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const setItem = useCallback(async (size_variant_id, quantity, opts = {}) => {
    const moreRequested = !!opts.more_requested;
    // optimistic
    setCart((c) => ({
      ...c,
      items: (() => {
        const rest = c.items.filter((i) => i.size_variant_id !== size_variant_id);
        if (quantity <= 0) return rest;
        const existing = c.items.find((i) => i.size_variant_id === size_variant_id);
        const base = existing || { size_variant_id };
        return [...rest, { ...base, quantity, more_requested: moreRequested }];
      })(),
    }));
    const { data } = await api.post("/cart/items", {
      size_variant_id, quantity, more_requested: moreRequested,
    });
    setCart(data);
    return data;
  }, []);

  const clear = useCallback(async () => {
    const { data } = await api.delete("/cart");
    setCart(data);
  }, []);

  const totalUnits = (cart.items || []).reduce((s, i) => s + i.quantity, 0);

  return (
    <CartCtx.Provider value={{ cart, loading, setItem, clear, reload, totalUnits }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
